<script setup lang="ts">
import { AiCompanionReviewModeEnum } from "@simbelapp/order-sdk";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import CopilotReviewCard from "~/components/Copilot/blocks/CopilotReviewCard.vue";
import CopilotToolAction from "~/components/Copilot/blocks/CopilotToolAction.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { copilotBlockOf } from "~/utils/helpers/copilot/blocks";
import { isFinishedTrainingReview, visiblePartsOf } from "~/utils/helpers/copilot/message-parts";
import {
  CopilotDataPartEnum,
  CopilotPartEnum,
  CopilotToolEnum,
  CopilotToolStateEnum,
} from "~/utils/enums/copilot/chat.enums";
import { CopilotErrorCodeEnum, type CopilotErrorRecoveryEnum } from "~/utils/enums/copilot/errors.enums";
import { resolveStepLabels } from "~/utils/helpers/copilot/analysis";
import { normalizeCopilotError } from "~/utils/helpers/copilot/errors";
import { localizeCopilotMessageHtml, localizeCopilotMessageText } from "~/utils/helpers/copilot/status-text";
import { sanitizeHtml } from "~/utils/helpers/sanitize";
import type {
  ICopilotAction,
  ICopilotApproval,
  ICopilotTools,
  ICopilotTrainingReview,
  ICopilotUIMessage,
} from "~/utils/interfaces/copilot-interfaces";
import type { ICopilotAnalysis } from "~/utils/interfaces/copilot-blocks-interfaces";

const props = defineProps<{
  message: ICopilotUIMessage;
  busy?: boolean;
  canRetry?: boolean;
}>();

const emit = defineEmits<{
  "pick-action": [action: ICopilotAction];
  approve: [payload: ICopilotApproval];
  deny: [payload: ICopilotApproval];
  "open-review": [review: ICopilotTrainingReview];
  "error-action": [recovery: CopilotErrorRecoveryEnum];
  retry: [];
}>();

const ANALYSIS_PART_TYPE = `data-${CopilotDataPartEnum.ANALYSIS}`;
const ERROR_PART_TYPE = `data-${CopilotDataPartEnum.ERROR}`;
const isAttendanceBlocked = computed(() =>
  props.message.parts.some(
    (part) =>
      part.type === ERROR_PART_TYPE && normalizeCopilotError(part).code === CopilotErrorCodeEnum.MULTI_TIMESLOT_SESSION,
  ),
);

/**
 * Walks the parts in stream order — the backend decides the layout — and resolves each one
 * against the block registry. A part type the front does not know about renders nothing
 * instead of breaking the bubble.
 */
const parts = computed(() => {
  // The review mode (create/update) determines the step labels; it is only known once the
  // tool output arrives, so while steps are streaming we default to create labels.
  let reviewMode: AiCompanionReviewModeEnum | null = null;
  for (const part of props.message.parts) {
    if (isToolUIPart(part) && getToolName(part) === CopilotToolEnum.REVIEW_TRAINING) {
      if (part.state === CopilotToolStateEnum.OUTPUT_AVAILABLE) {
        reviewMode = (part.output as ICopilotTrainingReview).mode;
      }
    }
  }

  // `visiblePartsOf` decides what paints, so an assistant message left with nothing gets no bubble.
  return visiblePartsOf(props.message).map((part) => {
    const block = copilotBlockOf(part.type);
    if (!block || !("data" in part)) return { part, component: null, props: {}, listeners: {} };

    const isErrorBlock = part.type === ERROR_PART_TYPE;
    const data = isErrorBlock
      ? normalizeCopilotError(part.data)
      : part.type === ANALYSIS_PART_TYPE
        ? resolveStepLabels(part.data as ICopilotAnalysis, reviewMode)
        : part.data;

    return {
      part,
      component: block.component,
      props: {
        [block.prop]: data,
        ...(block.interactive || isErrorBlock ? { disabled: props.busy } : {}),
        ...(isErrorBlock ? { canRetry: props.canRetry } : {}),
      },
      listeners: block.interactive
        ? { pick: (action: ICopilotAction) => emit("pick-action", action) }
        : isErrorBlock
          ? { recover: (recovery: CopilotErrorRecoveryEnum) => emit("error-action", recovery) }
          : {},
    };
  });
});

function fileNameOf(part: ICopilotUIMessage["parts"][number]): string {
  return "filename" in part && part.filename ? part.filename : "document";
}

/**
 * The finished `reviewTraining` output, or null. A plain function: the AI SDK mutates tool parts
 * in place, so a computed would never see the state reach "output-available".
 */
function reviewOf(part: ICopilotUIMessage["parts"][number]): ICopilotTrainingReview | null {
  if (!isFinishedTrainingReview(part) || !("output" in part)) return null;

  return part.output as ICopilotTrainingReview;
}

/**
 * The backend may answer in HTML. Tag-free text keeps the interpolation path — safer, and its
 * `whitespace-pre-wrap` preserves the newlines; markup goes through DOMPurify before `v-html`.
 */
const HTML_TAG_REGEX = /<\/?[a-z][^>]*>/i;

function isHtmlText(text: string): boolean {
  return !isAttendanceBlocked.value && HTML_TAG_REGEX.test(text);
}

function localizedTextOf(text: string): string {
  if (isAttendanceBlocked.value) return text;
  return localizeCopilotMessageText(props.message, text);
}

function localizedHtmlOf(text: string): string {
  return localizeCopilotMessageHtml(props.message, sanitizeHtml(text));
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <template v-for="(entry, index) of parts" :key="`${entry.part.type}-${index}`">
      <!-- eslint-disable vue/no-v-html -- sanitized via DOMPurify -->
      <div
        v-if="entry.part.type === CopilotPartEnum.TEXT && isHtmlText(entry.part.text)"
        class="copilot_html body_regular text-primary-color"
        v-html="localizedHtmlOf(entry.part.text)"
      />
      <!-- eslint-enable vue/no-v-html -->

      <span
        v-else-if="entry.part.type === CopilotPartEnum.TEXT"
        class="body_regular whitespace-pre-wrap text-primary-color"
        >{{ localizedTextOf(entry.part.text) }}</span
      >

      <div
        v-else-if="entry.part.type === CopilotPartEnum.FILE"
        class="flex w-fit items-center gap-[9px] rounded-xl border border-stroke-color bg-light-color px-3 py-[9px]"
      >
        <span class="flex h-8 w-[26px] shrink-0 items-center justify-center rounded-md bg-light-blue-color">
          <SvgIcon name="document" class="h-[14px] w-[14px] text-primary-blue-color" />
        </span>
        <span class="meta_regular leading-[18px] text-primary-color">{{ fileNameOf(entry.part) }}</span>
      </div>

      <CopilotReviewCard
        v-else-if="reviewOf(entry.part)"
        :review="reviewOf(entry.part)!"
        @open="emit('open-review', $event)"
      />

      <CopilotToolAction
        v-else-if="isToolUIPart(entry.part)"
        :part="entry.part as ToolUIPart<ICopilotTools>"
        :disabled="busy"
        :can-retry="canRetry"
        @approve="emit('approve', $event)"
        @deny="emit('deny', $event)"
        @retry="emit('retry')"
      />

      <component :is="entry.component" v-else-if="entry.component" v-bind="entry.props" v-on="entry.listeners" />
    </template>
  </div>
</template>

<style scoped lang="scss">
/* Backend-provided markup: `v-html` children carry no scope attribute (hence `:deep()`),
 * and Tailwind's reset has stripped their native list/link/heading/table styles. */
.copilot_html {
  :deep(p),
  :deep(ul),
  :deep(ol),
  :deep(pre),
  :deep(table) {
    margin-bottom: 8px;
  }

  :deep(> :last-child) {
    margin-bottom: 0;
  }

  :deep(ul) {
    list-style: disc;
    padding-left: 20px;
  }

  :deep(ol) {
    list-style: decimal;
    padding-left: 20px;
  }

  :deep(li) {
    margin-bottom: 2px;
  }

  :deep(a) {
    color: var(--primary-blue-color);
    text-decoration: underline;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4) {
    font-weight: 600;
    margin-bottom: 4px;
  }

  :deep(code) {
    background-color: var(--grey-light);
    border: 1px solid var(--grey-lightest);
    border-radius: 4px;
    padding: 1px 4px;
  }

  :deep(pre) {
    background-color: var(--grey-light);
    border: 1px solid var(--grey-lightest);
    border-radius: 8px;
    padding: 8px 12px;
    overflow-x: auto;

    code {
      background: none;
      border: none;
      padding: 0;
    }
  }

  :deep(table) {
    display: block;
    border-collapse: collapse;
    overflow-x: auto;
  }

  :deep(th),
  :deep(td) {
    border: 1px solid var(--grey-lightest);
    padding: 4px 8px;
    text-align: left;
    vertical-align: top;
  }

  :deep(th) {
    background-color: var(--grey-light);
    font-weight: 600;
  }
}
</style>
